import { useState, useMemo } from 'react';
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CalendarDays, ChevronDown, Globe } from 'lucide-react';
import { RansomwareItem } from '@/types/ransomware';
import { useLanguage } from '@/hooks/useLanguage';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

interface RansomwareGroupDetailProps {
    groupName: string;
    ransomwareData: RansomwareItem[];
}

// Flag emojis for common countries (reused)
const countryFlags: Record<string, string> = {
    'US': '🇺🇸', 'UK': '🇬🇧', 'CA': '🇨🇦', 'AU': '🇦🇺', 'DE': '🇩🇪',
    'FR': '🇫🇷', 'IT': '🇮🇹', 'ES': '🇪🇸', 'JP': '🇯🇵', 'CN': '🇨🇳',
    'RU': '🇷🇺', 'BR': '🇧🇷', 'IN': '🇮🇳', 'PL': '🇵🇱',
};

const INITIAL_VICTIMS_COUNT = 9;
const LOAD_MORE_COUNT = 20;

export function RansomwareGroupDetail({ groupName, ransomwareData }: RansomwareGroupDetailProps) {
    const { t, language } = useLanguage();
    const [visibleVictimsCount, setVisibleVictimsCount] = useState(INITIAL_VICTIMS_COUNT);

    // Filter victims for this group
    const groupVictims = useMemo(() => {
        if (!groupName || !ransomwareData) return [];
        return ransomwareData
            .filter(item => item.group === groupName)
            .sort((a, b) => new Date(b.attackdate).getTime() - new Date(a.attackdate).getTime());
    }, [groupName, ransomwareData]);

    const visibleVictims = useMemo(() => {
        return groupVictims.slice(0, visibleVictimsCount);
    }, [groupVictims, visibleVictimsCount]);

    const handleLoadMore = () => {
        setVisibleVictimsCount(prev => prev + LOAD_MORE_COUNT);
    };

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            if (language === 'en') {
                return format(date, 'MMM d, yyyy');
            } else {
                return format(date, 'd MMM yyyy', { locale: pl });
            }
        } catch (e) {
            return dateString;
        }
    };

    const getCountryFlag = (countryCode: string) => {
        return countryFlags[countryCode] || '🏳️';
    };

    return (
        <div className="space-y-8 animate-fade-in">
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-3xl font-bold flex items-center gap-3">
                            {groupName}
                        </h2>
                        <p className="text-muted-foreground mt-1">
                            {language === 'en' ? 'Detailed group intelligence' : 'Szczegółowe informacje o grupie'}
                        </p>
                    </div>
                </div>
            </div>

            <Separator />

            {/* Victims List Section */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold flex items-center gap-2">
                        <AlertTriangle className="h-6 w-6 text-destructive" />
                        {language === 'en' ? 'Known Victims' : 'Znane Ofiary'}
                        <Badge variant="secondary" className="ml-2 text-base px-2">
                            {groupVictims.length}
                        </Badge>
                    </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {visibleVictims.map((victim, index) => (
                        <div key={index} className="p-4 bg-card border rounded-lg hover:shadow-md transition-all hover:-translate-y-1 group">
                            <div className="flex justify-between items-start mb-2">
                                <span className="font-semibold truncate pr-2" title={victim.victim}>{victim.victim}</span>
                                <Badge variant="outline" className="font-normal shrink-0">
                                    {getCountryFlag(victim.country)} {victim.country}
                                </Badge>
                            </div>
                            <div className="space-y-2 text-sm text-muted-foreground">
                                <div className="flex items-center gap-2">
                                    <CalendarDays className="h-4 w-4" />
                                    {formatDate(victim.attackdate)}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Globe className="h-4 w-4" />
                                    <span className="truncate">
                                        {(() => {
                                            try {
                                                const url = new URL(victim.domain.startsWith('http') ? victim.domain : `http://${victim.domain}`);
                                                return url.hostname;
                                            } catch {
                                                return victim.domain?.split('/')[0] || '';
                                            }
                                        })()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {visibleVictimsCount < groupVictims.length && (
                    <div className="flex justify-center pt-4">
                        <Button variant="outline" onClick={handleLoadMore} className="gap-2">
                            {language === 'en' ? 'Show More Victims' : 'Pokaż więcej ofiar'}
                            <ChevronDown className="h-4 w-4" />
                        </Button>
                    </div>
                )}

                {groupVictims.length === 0 && (
                    <div className="p-12 text-center text-muted-foreground bg-card border rounded-lg border-dashed">
                        <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-20" />
                        <p>{language === 'en' ? 'No victims found in database.' : 'Nie znaleziono ofiar w bazie danych.'}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
